import {
  ChangeEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {styled} from 'styled-components';
import {Redirect, useLocation, useParams} from 'wouter';

import {ProjectSex} from '@shared/api/app_api';
import {removeUndefined} from '@shared/lib/type_utils';

import {Button, NavButton} from '@shared-web/components/core/button';

import {getScoreInfo} from '@src/components/score';
import {TopBarApp} from '@src/components/top_bar_app';
import {apiCall} from '@src/lib/api';
import {ParsedDb, useDb} from '@src/lib/db_store';
import {
  ParsedProject,
  parseProject,
  ProjectScores,
  setProjectCompareCode,
  useProjects,
} from '@src/lib/projects_stores';
import {LargeNavButton} from '@src/theme';

// function cleanName(name: string): string {
//   for (const suffix of ['-boy', '-girl']) {
//     if (name.endsWith(suffix)) {
//       return name.slice(0, -suffix.length);
//     }
//   }
//   return name;
// }

function sortScores(
  db: ParsedDb | undefined,
  projectScores: ProjectScores,
  compareScores?: ProjectScores
): [string, number][] {
  if (!compareScores) {
    return removeUndefined(
      db?.map(n => {
        const score = projectScores[n.key];
        if (score === undefined) {
          return undefined;
        }
        return [n.key, score];
      }) ?? []
    );
  }
  const commonScores = Object.entries(projectScores)
    .filter(s => compareScores[s[0]] !== undefined)
    .sort((e1, e2) => {
      const e1Compare = compareScores[e1[0]] ?? 0;
      const e2Compare = compareScores[e2[0]] ?? 0;
      const scoreDiff = e2[1] + e2Compare - (e1[1] + e1Compare);
      return scoreDiff !== 0 ? scoreDiff : e1[0].localeCompare(e2[0]);
    });
  const notCommonScores = Object.entries(projectScores)
    .filter(s => compareScores[s[0]] === undefined)
    .sort((s1, s2) => {
      const scoreDiff = s2[1] - s1[1];
      return scoreDiff !== 0 ? scoreDiff : s1[0].localeCompare(s2[0]);
    });
  return [...commonScores, ...notCommonScores];
}

export const ProjectPage: React.FC = () => {
  const projects = useProjects();
  const {id = ''} = useParams();
  const [code, setCode] = useState('');
  const [compareProject, setCompareProject] = useState<ParsedProject | undefined>();

  const project = projects.find(p => p.id === id);
  const db = useDb((project?.settings.sexes ?? []) as ProjectSex[]);

  const [_, setLocation] = useLocation();

  const handleClick = useCallback<MouseEventHandler>(
    evt => {
      const key = evt.currentTarget.getAttribute('data-key');
      if (key === null) {
        return;
      }
      setLocation(`/project/${id}/score/${key}`);
    },
    [id, setLocation]
  );

  useEffect(() => {
    if (project?.compare !== undefined) {
      apiCall('GET /get-project', {code: project.compare})
        .then(res => {
          setCompareProject(parseProject(res));
        })
        .catch((err: unknown) => console.error(err));
    }
  }, [project?.compare]);

  const handleCodeChange = useCallback<ChangeEventHandler<HTMLInputElement>>(evt => {
    setCode(evt.currentTarget.value);
  }, []);

  const handleCancelCompareClick = useCallback(() => {
    setProjectCompareCode(id, undefined);
  }, [id]);

  const handleCompareClick = useCallback(() => {
    if (code.length === 0) {
      return;
    }
    setProjectCompareCode(id, code.trim().toUpperCase());
  }, [code, id]);

  const nameByKey = useMemo(() => {
    const nameByKey = new Map<string, string>();
    for (const {key, totalByName} of db ?? []) {
      const mostUsedName = totalByName[0]?.name;
      if (mostUsedName !== undefined) {
        nameByKey.set(key, mostUsedName);
      }
    }
    return nameByKey;
  }, [db]);

  if (!project) {
    console.log('ProjectPage: project not found');
    return <Redirect to={'/'} />;
  }

  if (Object.keys(project.scores).length === 0) {
    return (
      <TopBarApp left={<NavButton to="/">Retour</NavButton>} right="Nouvelle recherche">
        <InitialScreenWrapper>
          <InitialScreen>
            <LargeNavButton to={`/project/${id}/score`}>Noter des prénoms</LargeNavButton>
          </InitialScreen>
        </InitialScreenWrapper>
      </TopBarApp>
    );
  }

  return (
    <TopBarApp
      left={<NavButton to="/">Retour</NavButton>}
      center={
        <Title>
          <LabelScore>{Object.keys(project.scores).length.toLocaleString()}</LabelScore>
          <LightLabel>
            {db === undefined ? `prénoms notés` : `/ ${Object.keys(db).length.toLocaleString()}`}
          </LightLabel>
        </Title>
      }
      right={<NavButton to={`/project/${id}/score`}>Continuer</NavButton>}
    >
      <Wrapper>
        {project.compare === undefined ? (
          <></>
        ) : (
          <CompareLine>
            {`Comparaison avec ${project.compare}`}
            <Button onClick={handleCancelCompareClick}>Annuler</Button>
          </CompareLine>
        )}
        <Content>
          <NameList>
            {sortScores(db, project.scores, compareProject?.scores).map(([key, score]) => {
              const {color} = getScoreInfo(score);
              const name = nameByKey.get(key) ?? key;
              return (
                <Name data-key={key} key={name} $color={color} onClick={handleClick}>
                  {name}
                </Name>
              );
            })}
          </NameList>
          <OtherList>
            {project.compare === undefined ? (
              <>
                <CompareInfo>Entrez le code du projet à comparer.</CompareInfo>
                <CodeInput
                  value={code}
                  onChange={handleCodeChange}
                  type="text"
                  placeholder="CODE"
                />
                <Button onClick={handleCompareClick}>Comparer</Button>
                <CompareInfo2>{`(votre code: ${project.code})`}</CompareInfo2>
              </>
            ) : !compareProject ? (
              <LoadingMessage>Chargement...</LoadingMessage>
            ) : (
              sortScores(db, compareProject.scores, project.scores).map(([phonetic, score]) => {
                const {color} = getScoreInfo(score);
                const name = nameByKey.get(phonetic) ?? phonetic;
                return (
                  <Name data-key={name} key={name} $color={color} onClick={handleClick}>
                    {name}
                  </Name>
                );
              })
            )}
          </OtherList>
        </Content>
      </Wrapper>
    </TopBarApp>
  );
};
ProjectPage.displayName = 'ProjectPage';

const InitialScreenWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding-bottom: 1rem;
  box-sizing: border-box;
`;

const InitialScreen = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div``;

const Content = styled.div`
  display: flex;
  padding: 1rem;
  width: 100%;
  margin-top: 1rem;
  gap: 1rem;
`;

const NameList = styled.div`
  width: 0;
  flex-grow: 1;
  flex-basis: 1px;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const OtherList = styled.div`
  width: 0;
  flex-grow: 1;
  flex-basis: 1px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.5rem;
`;

const Name = styled.div<{$color: string}>`
  background-color: ${p => p.$color};
  color: white;
  border-radius: 0.4rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  text-shadow:
    0.05rem 0.05rem 0 #000,
    -0.05rem 0.05rem 0 #000,
    -0.05rem -0.05rem 0 #000,
    0.05rem -0.05rem 0 #000;
`;

const Title = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
`;

const LabelScore = styled.div`
  font-size: 2rem;
`;

const LightLabel = styled.div`
  font-size: 1.2rem;
  color: #c4c4c4;
`;

const CodeInput = styled.input`
  padding: 2%;
  text-align: center;
  font-size: 2rem;
`;

const LoadingMessage = styled.div``;

const CompareInfo = styled.div``;
const CompareInfo2 = styled.div``;

const CompareLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.3rem;
`;
